import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import { loadAdminList } from "../../api/adminApi";
import { toggleUserStatus, changeUserPassword } from "../../api/settingApi";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey, faEdit, faLock, faLockOpen, faPlus, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';

Modal.setAppElement('#root');

const AdminUsers = () => {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    fetchAdmins();
  }, []);

  const getStatusBadge = (status) => {
    if (status === "Active") {
      return (
        <span className="px-2 py-1 text-xs rounded-full bg-green-200 text-green-800 font-semibold">
          Active
        </span>
      );
    } else if (status === "Inactive") {
      return (
        <span className="px-2 py-1 text-xs rounded-full bg-red-200 text-red-800 font-semibold">
          Inactive
        </span>
      );
    } else {
      return (
        <span className="px-2 py-1 text-xs rounded-full bg-gray-200 text-gray-700 font-semibold">
          Unknown
        </span>
      );
    }
  };

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const response = await loadAdminList();
      setList(response.data);
    } catch (error) {
      console.error("Error loading admins", error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (adminId) => {
    try {
      await toggleUserStatus(adminId);
      setList(prevList => prevList.map(admin =>
        admin.id === adminId
          ? { ...admin, status: admin.status === 'Active' ? 'Inactive' : 'Active' }
          : admin
      ));
    } catch (error) {
      console.error("Failed to toggle status", error);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!selectedAdmin?.id) {
      setError("User ID not set.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d]{8,50}$/;
    if (!regex.test(newPassword)) {
      setError("Password must be between 8 and 50 characters long, contain at least one uppercase letter, one lowercase letter, and one number.");
      return;
    }

    try {
      await changeUserPassword({
        userId: selectedAdmin.id,
        passwordString: newPassword
      });
      setSuccess("Password changed successfully.");
      setTimeout(() => {
        closeModal();
      }, 1500);
    } catch (err) {
      setError("Failed to change password.");
    }
  };

  const openModal = (admin) => {
    setSelectedAdmin(admin);
    setNewPassword("");
    setConfirmPassword("");
    setError("");
    setSuccess("");
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setSelectedAdmin(null);
    setError("");
    setSuccess("");
    setNewPassword("");
    setConfirmPassword("");
  };

  if (loading) {
    return <div className="text-center py-8 text-blue-600 font-semibold">Loading...</div>;
  }

  return (<>
    <div className="bg-blue-50 p-8 min-h-screen">
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <h2 className="text-3xl font-bold text-blue-800">Admin Users</h2>
          <button
            className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 shadow-md flex items-center space-x-2"
            onClick={() => navigate('/admin/add-admin')}
          >
            <FontAwesomeIcon icon={faPlus} />
            <span>Add Admin</span>
          </button>
        </div>

        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-blue-100">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-bold text-blue-700 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-sm font-bold text-blue-700 uppercase">Username</th>
                <th className="px-6 py-3 text-left text-sm font-bold text-blue-700 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-sm font-bold text-blue-700 uppercase">Organization</th>
                <th className="px-6 py-3 text-left text-sm font-bold text-blue-700 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-sm font-bold text-blue-700 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {list.map((e) => (
                <tr key={e.id} className="hover:bg-blue-50 transition-all">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{e.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{e.username}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{e.email}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{e.organization}</td>
                  <td className="px-6 py-4 text-sm">{getStatusBadge(e.status)}</td>
                  <td className="px-6 py-4 text-right text-sm space-x-2">
                    <button
                      className="border px-2 py-1 rounded text-red-600 border-red-600 hover:text-white hover:bg-red-600 transition"
                      title={e.status === "Active" ? "Lock User" : "Unlock User"}
                      onClick={() => handleToggleStatus(e.id)}
                    >
                      <FontAwesomeIcon icon={e.status === "Active" ? faLockOpen : faLock} />
                    </button>

                    <button
                      className="border px-2 py-1 rounded text-green-600 border-green-600 hover:text-white hover:bg-green-600 transition"
                      title="Update"
                      onClick={() => {/* your update handler here */ }}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>

                    <button
                      className="border px-2 py-1 rounded text-blue-600 border-blue-600 hover:text-white hover:bg-blue-600 transition"
                      title="Change Password"
                      onClick={() => openModal(e)}
                    >
                      <FontAwesomeIcon icon={faKey} />
                    </button>
                  </td>
                </tr>
              ))}
              {list.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center text-gray-500 py-6">
                    No admin users found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Change Password Modal"
        className="bg-white p-6 max-w-md mx-auto mt-20 rounded-lg shadow-lg outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-start"
      >
        <h2 className="text-xl font-semibold text-blue-600 mb-4">Change Password for {selectedAdmin?.name}</h2>

        {error && <div className="bg-red-500 text-white px-4 py-2 rounded mb-3">{error}</div>}
        {success && <div className="bg-green-500 text-white px-4 py-2 rounded mb-3">{success}</div>}

        <form onSubmit={handlePasswordChange}>
          <div className="mb-4 relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <input
              type={showNewPassword ? "text" : "password"}
              className="w-full border border-gray-300 px-3 py-2 rounded pr-10"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              placeholder="Enter new password"
            />
            <button
              type="button"
              className="absolute right-3 top-9 text-gray-500 hover:text-gray-700"
              onClick={() => setShowNewPassword(!showNewPassword)}
              tabIndex={-1}
            >
              <FontAwesomeIcon icon={showNewPassword ? faEyeSlash : faEye} />
            </button>
          </div>
          <div className="mb-4 relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
            <input
              type={showConfirmPassword ? "text" : "password"}
              className="w-full border border-gray-300 px-3 py-2 rounded pr-10"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder="Confirm new password"
            />
            <button
              type="button"
              className="absolute right-3 top-9 text-gray-500 hover:text-gray-700"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              tabIndex={-1}
            >
              <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
            </button>
          </div>
          <div className="flex justify-end space-x-3">
            <button type="button" onClick={closeModal} className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400">
              Cancel
            </button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
              Save
            </button>
          </div>
        </form>
      </Modal>
    </div>
  </>
  );

};

export default AdminUsers;
